import React from "react";

import { usePickingListSummary } from "./usePickingListSummary";

/**
 * Hook for managing pallet reservations in picking list.
 * Handles selection state, backend sync, and reservation logic (whole pallets only).
 */
export const usePickingListReservation = ({ orderId }) => {
	const [selectedPalletsForBackend, setSelectedPalletsForBackend] = React.useState([]);
	const [unselectedReservedPallets, setUnselectedReservedPallets] = React.useState([]);

	// Tracks pallet.quantity for every pallet the user has interacted with, so box counts
	// stay correct after paginating/filtering (useListContext data only covers the current page).
	const [palletBoxCounts, setPalletBoxCounts] = React.useState({});

	const trackPalletBoxCount = (pallet) => {
		if (!pallet || pallet.id == null) return;
		setPalletBoxCounts((prev) =>
			prev[pallet.id] === (pallet.quantity || 0)
				? prev
				: { ...prev, [pallet.id]: pallet.quantity || 0 }
		);
	};

	// UI state - checkbox display state
	const [selectedPalletsUI, setSelectedPalletsUI] = React.useState({});

	const { summaryTotals: reservationTotals, refetch: fetchReservationTotals } =
		usePickingListSummary({
			orderId,
		});

	// --------------------------------- Pallet Reservation logic ---------------------------------
	const reservePallet = (pallet) => {
		const palletId = pallet.id;
		trackPalletBoxCount(pallet);
		setSelectedPalletsUI((prev) => ({ ...prev, [palletId]: true }));
		// if pallet is selected again remove it from unselection list
		if (pallet.reserved_order_id !== null) {
			setUnselectedReservedPallets((prev) => prev.filter((id) => id !== palletId));
		}
		setSelectedPalletsForBackend((prev) => (prev.includes(palletId) ? prev : [...prev, palletId]));
	};

	const unreservePallet = (pallet) => {
		const palletId = pallet.id;
		trackPalletBoxCount(pallet);
		setSelectedPalletsUI((prev) => ({ ...prev, [palletId]: false }));
		setSelectedPalletsForBackend((prev) => prev.filter((id) => id !== palletId));

		// If pallet was reserved, track it for unselection
		if (pallet.reserved_order_id !== null) {
			setUnselectedReservedPallets((prev) =>
				prev.includes(palletId) ? prev : [...prev, palletId]
			);
		}
	};

	const handlePalletReservationSelection = (pallet) => {
		if (selectedPalletsUI[pallet.id]) {
			unreservePallet(pallet);
		} else {
			reservePallet(pallet);
		}
	};

	const clearBackendSelections = () => {
		setSelectedPalletsForBackend([]);
		setUnselectedReservedPallets([]);
		setPalletBoxCounts({});
	};

	const clearAllSelections = React.useCallback(() => {
		clearBackendSelections();
		setSelectedPalletsUI({});
	}, [clearBackendSelections]);

	return {
		selectedPalletsForBackend,
		unselectedReservedPallets,
		selectedPalletsUI,
		reservationTotals,
		palletBoxCounts,
		setSelectedPalletsUI,
		setSelectedPalletsForBackend,
		setUnselectedReservedPallets,
		handlePalletReservationSelection,
		clearBackendSelections,
		clearAllSelections,
		fetchReservationTotals,
	};
};
